/**
 * Dead Code Check Module
 * Detects unused functions, imports, variables, and legacy code
 */

import type { CheckContext, CodeReviewFinding } from '../types.js';
import { generateFindingId } from '../types.js';

// Patterns for detecting dead code

// Commented out code patterns
const COMMENTED_CODE_PATTERNS = [
  {
    pattern: /\/\/\s*(?:if|for|while|switch|return|var|let|const|public|private|async)\s+/gi,
    name: 'Commented code block',
  },
  {
    pattern: /\/\*[\s\S]*?(?:if|for|while|return|function)[\s\S]*?\*\//gi,
    name: 'Commented code block',
  },
];

// TODO/FIXME patterns (potential dead code indicators)
const TODO_PATTERNS = [
  {
    pattern: /\/\/\s*TODO[:\s]/gi,
    name: 'TODO comment',
  },
  {
    pattern: /\/\/\s*FIXME[:\s]/gi,
    name: 'FIXME comment',
  },
  {
    pattern: /\/\/\s*HACK[:\s]/gi,
    name: 'HACK comment',
  },
  {
    pattern: /\/\/\s*XXX[:\s]/gi,
    name: 'XXX comment',
  },
];

// C# unused patterns
const CSHARP_UNUSED_IMPORT_PATTERN = /using\s+([\w.]+);/gi;
const CSHARP_PRIVATE_METHOD_PATTERN = /private\s+(?:async\s+)?(?:static\s+)?(?:Task<?\w*>?|\w+)\s+(\w+)\s*\(/gi;
const CSHARP_PRIVATE_FIELD_PATTERN = /private\s+(?:readonly\s+)?(?:static\s+)?\w+(?:<[^>]+>)?\s+_?(\w+)\s*[;=]/gi;

// TypeScript/JavaScript unused patterns - reserved for future use
// TS_IMPORT_PATTERN: /import\s+(?:\{([^}]+)\}|(\w+))\s+from/gi
// TS_CONST_PATTERN: /(?:const|let)\s+(\w+)\s*=/gi
// TS_FUNCTION_PATTERN: /(?:function\s+(\w+)|const\s+(\w+)\s*=\s*(?:async\s*)?\([^)]*\)\s*=>)/gi

export async function checkDeadCode(context: CheckContext): Promise<CodeReviewFinding[]> {
  const findings: CodeReviewFinding[] = [];

  for (const file of context.files) {
    const lines = file.content.split('\n');

    // Check for commented code
    for (const { pattern } of COMMENTED_CODE_PATTERNS) {
      let match;
      pattern.lastIndex = 0;
      while ((match = pattern.exec(file.content)) !== null) {
        const lineNumber = getLineNumber(file.content, match.index);
        const lineContent = lines[lineNumber - 1]?.trim() || '';

        // Skip if it looks like documentation
        if (lineContent.includes('///') || lineContent.includes('/**')) continue;

        findings.push({
          id: generateFindingId('dead-code'),
          category: 'dead-code',
          severity: 'warning',
          title: 'Commented Code',
          description: 'Commented-out code should be removed. Use version control to preserve history.',
          file: file.relativePath,
          line: lineNumber,
          code: truncateCode(lineContent),
          suggestion: 'Remove commented code. If needed later, it can be recovered from git history.',
          autoFixable: true,
        });
      }
    }

    // Check for TODO/FIXME comments
    for (const { pattern, name } of TODO_PATTERNS) {
      let match;
      pattern.lastIndex = 0;
      while ((match = pattern.exec(file.content)) !== null) {
        const lineNumber = getLineNumber(file.content, match.index);
        const lineContent = lines[lineNumber - 1]?.trim() || '';

        findings.push({
          id: generateFindingId('dead-code'),
          category: 'dead-code',
          severity: 'info',
          title: `${name} Found`,
          description: `${name} indicates incomplete or temporary code that may need attention.`,
          file: file.relativePath,
          line: lineNumber,
          code: truncateCode(lineContent),
          suggestion: 'Address the TODO/FIXME or create a ticket to track it.',
          autoFixable: false,
        });
      }
    }

    // C# specific dead code checks
    if (file.language === 'csharp') {
      // Check for unused private methods
      const privateMethods = new Map<string, number>();
      let match;

      CSHARP_PRIVATE_METHOD_PATTERN.lastIndex = 0;
      while ((match = CSHARP_PRIVATE_METHOD_PATTERN.exec(file.content)) !== null) {
        privateMethods.set(match[1], getLineNumber(file.content, match.index));
      }

      // Check if methods are called
      for (const [methodName, lineNumber] of privateMethods) {
        // Count occurrences (should be at least 2: definition + call)
        const regex = new RegExp(`\\b${methodName}\\s*\\(`, 'g');
        const occurrences = (file.content.match(regex) || []).length;

        if (occurrences === 1) {
          findings.push({
            id: generateFindingId('dead-code'),
            category: 'dead-code',
            severity: 'warning',
            title: 'Unused Private Method',
            description: `Private method '${methodName}' is declared but never called.`,
            file: file.relativePath,
            line: lineNumber,
            suggestion: 'Remove the unused method or make it public if it should be accessible.',
            autoFixable: true,
          });
        }
      }

      // Check for unused private fields
      const privateFields = new Map<string, number>();

      CSHARP_PRIVATE_FIELD_PATTERN.lastIndex = 0;
      while ((match = CSHARP_PRIVATE_FIELD_PATTERN.exec(file.content)) !== null) {
        const fieldName = match[1];
        // Skip common patterns
        if (fieldName.startsWith('_') && fieldName.length > 1) {
          privateFields.set(fieldName, getLineNumber(file.content, match.index));
        }
      }

      for (const [fieldName, lineNumber] of privateFields) {
        const regex = new RegExp(`\\b${fieldName}\\b`, 'g');
        const occurrences = (file.content.match(regex) || []).length;

        if (occurrences === 1) {
          findings.push({
            id: generateFindingId('dead-code'),
            category: 'dead-code',
            severity: 'warning',
            title: 'Unused Private Field',
            description: `Private field '${fieldName}' is declared but never used.`,
            file: file.relativePath,
            line: lineNumber,
            suggestion: 'Remove the unused field.',
            autoFixable: true,
          });
        }
      }

      // Check for unused using statements (simplified)
      checkUnusedCsharpUsings(file, lines, findings);
    }

    // TypeScript/JavaScript specific checks
    if (['typescript', 'javascript', 'tsx', 'jsx'].includes(file.language)) {
      checkUnusedJsImports(file, lines, findings);
      checkUnusedJsVariables(file, lines, findings);
    }
  }

  return findings;
}

function checkUnusedCsharpUsings(
  file: { relativePath: string; content: string },
  _lines: string[],
  findings: CodeReviewFinding[]
): void {
  const usings = new Map<string, number>();

  let match;
  CSHARP_UNUSED_IMPORT_PATTERN.lastIndex = 0;
  while ((match = CSHARP_UNUSED_IMPORT_PATTERN.exec(file.content)) !== null) {
    const namespace = match[1];
    usings.set(namespace, getLineNumber(file.content, match.index));
  }

  // Get content after usings
  const contentAfterUsings = file.content.replace(/using\s+[\w.]+;/g, '');

  for (const [namespace, lineNumber] of usings) {
    // Get last part of namespace for checking
    const parts = namespace.split('.');
    const lastPart = parts[parts.length - 1];

    // Check if namespace or its last part is used
    const isUsed =
      contentAfterUsings.includes(lastPart) ||
      contentAfterUsings.includes(namespace);

    // Skip system namespaces and common always-needed ones
    const alwaysNeeded = ['System', 'System.Collections.Generic', 'System.Linq', 'System.Threading.Tasks'];
    if (alwaysNeeded.includes(namespace)) continue;

    if (!isUsed) {
      findings.push({
        id: generateFindingId('dead-code'),
        category: 'dead-code',
        severity: 'info',
        title: 'Potentially Unused Using',
        description: `The using '${namespace}' may not be needed in this file.`,
        file: file.relativePath,
        line: lineNumber,
        suggestion: 'Remove unused using statements to keep the file clean.',
        autoFixable: true,
      });
    }
  }
}

function checkUnusedJsImports(
  file: { relativePath: string; content: string },
  _lines: string[],
  findings: CodeReviewFinding[]
): void {
  // Extract imports
  const imports = new Map<string, { line: number; full: string }>();

  let match;
  const importPattern = /import\s+(?:\{([^}]+)\}|(\w+)(?:\s*,\s*\{([^}]+)\})?)\s+from\s+['"][^'"]+['"]/gi;

  while ((match = importPattern.exec(file.content)) !== null) {
    const lineNumber = getLineNumber(file.content, match.index);
    const namedImports = match[1] || match[3];
    const defaultImport = match[2];

    if (namedImports) {
      const names = namedImports.split(',').map((n) => {
        const parts = n.trim().split(/\s+as\s+/);
        return parts[parts.length - 1].trim();
      });

      for (const name of names) {
        if (name) imports.set(name, { line: lineNumber, full: match[0] });
      }
    }

    if (defaultImport && defaultImport !== 'React') {
      imports.set(defaultImport, { line: lineNumber, full: match[0] });
    }
  }

  // Get content after imports
  const contentAfterImports = file.content.replace(/import\s+.*from\s+['"][^'"]+['"]/g, '');

  for (const [name, { line }] of imports) {
    // Count occurrences
    const regex = new RegExp(`\\b${name}\\b`, 'g');
    const occurrences = (contentAfterImports.match(regex) || []).length;

    if (occurrences === 0) {
      findings.push({
        id: generateFindingId('dead-code'),
        category: 'dead-code',
        severity: 'warning',
        title: 'Unused Import',
        description: `Import '${name}' is declared but never used.`,
        file: file.relativePath,
        line,
        suggestion: 'Remove unused imports.',
        autoFixable: true,
      });
    }
  }
}

function checkUnusedJsVariables(
  file: { relativePath: string; content: string },
  _lines: string[],
  findings: CodeReviewFinding[]
): void {
  // This is a simplified check - only for obvious cases
  const varPattern = /(?:const|let)\s+(\w+)\s*=\s*(?!function|async\s+function)/gi;
  const variables = new Map<string, number>();

  let match;
  while ((match = varPattern.exec(file.content)) !== null) {
    const varName = match[1];
    // Skip common patterns
    if (varName.startsWith('_') || varName === 'i' || varName === 'j') continue;

    variables.set(varName, getLineNumber(file.content, match.index));
  }

  for (const [varName, lineNumber] of variables) {
    const regex = new RegExp(`\\b${varName}\\b`, 'g');
    const occurrences = (file.content.match(regex) || []).length;

    // Should appear at least twice (declaration + use)
    if (occurrences === 1) {
      findings.push({
        id: generateFindingId('dead-code'),
        category: 'dead-code',
        severity: 'warning',
        title: 'Unused Variable',
        description: `Variable '${varName}' is declared but never used.`,
        file: file.relativePath,
        line: lineNumber,
        suggestion: 'Remove unused variables or prefix with underscore if intentionally unused.',
        autoFixable: true,
      });
    }
  }
}

function getLineNumber(content: string, index: number): number {
  return content.slice(0, index).split('\n').length;
}

function truncateCode(code: string, maxLength: number = 100): string {
  if (code.length <= maxLength) return code;
  return code.slice(0, maxLength - 3) + '...';
}
